import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, HStack, IconButton, Input, Popover, Portal, Separator, Text, VStack } from "@chakra-ui/react";
import { LuCircleDot, LuLocateFixed, LuPause, LuPlay, LuRefreshCw, LuTrash2 } from "react-icons/lu";
import {
  BoundingSphere,
  CallbackProperty,
  Cartesian2,
  Cartesian3,
  Color,
  ColorBlendMode,
  CustomShader,
  DirectionalLight,
  HeadingPitchRange,
  HeadingPitchRoll,
  HorizontalOrigin,
  LabelStyle,
  LightingModel,
  Math as CesiumMath,
  PolylineDashMaterialProperty,
  ShadowMode,
  Transforms,
  VerticalOrigin,
  defined,
} from "cesium";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { showToast } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";

const POLL_MS = 10000;
const REQUEST_RENDER_MS = 250;
const OUTAGE_LABEL_OFFSET = new Cartesian2(0, -28);
const TRUCK_MODEL_URL = "https://matrix.gatik.ai/static/gatiktruck.glb";
const TRUCK_MODEL_SCALE = 1.0;
const MOCK_TRUCK_IDS = ["TRUCK_101", "TRUCK_202", "TRUCK_303"];
const EVENT_TYPE_STYLES = {
  hard_brake: { color: Color.ORANGE, short: "HB" },
  manual_mark: { color: Color.CYAN, short: "MM" },
  localization_degrade: { color: Color.YELLOW, short: "LD" },
  harsh_turn: { color: Color.MAGENTA, short: "HT" },
  default: { color: Color.WHITE, short: "EV" },
};

function parseTimestamp(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) return asNumber;
    const ms = Date.parse(value);
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

function formatOutage(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function htmlEscape(value) {
  return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function buildTruckDescription(truckId, point, outageStartedAt) {
  const headingDeg = Number.isFinite(point?.heading) ? Number(point.heading).toFixed(1) : "-";
  return `
    <div style="padding:8px;">
      <div style="font-size:16px;font-weight:700;margin-bottom:8px;">Truck ${htmlEscape(truckId)}</div>
      <div>Status: ${outageStartedAt == null ? "Live" : "Outage"}</div>
      <div>Heading: ${htmlEscape(headingDeg)}</div>
      <div>Speed: ${htmlEscape(point?.speed ?? "-")}</div>
      <div>Last point: ${point ? htmlEscape(new Date(point.timestamp).toISOString()) : "-"}</div>
    </div>
  `;
}

function buildEventDescription(event) {
  const props = event.properties || {};
  const rows = Object.entries({
    truckId: event.truckId,
    eventType: event.eventType,
    timestamp: event.timestamp ? new Date(event.timestamp).toISOString() : "",
    ...props,
  })
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `<tr><td style="padding:4px 8px;font-weight:600;">${htmlEscape(key)}</td><td style="padding:4px 8px;">${htmlEscape(value)}</td></tr>`)
    .join("");

  return `
    <div style="padding:8px;">
      <div style="font-size:15px;font-weight:700;margin-bottom:8px;">Live Event</div>
      <table style="border-collapse:collapse;">${rows}</table>
    </div>
  `;
}

function getTruckColor(truckId) {
  let hash = 0;
  for (let i = 0; i < truckId.length; i += 1) {
    hash = truckId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const r = ((hash >> 16) & 255) / 255;
  const g = ((hash >> 8) & 255) / 255;
  const b = (hash & 255) / 255;
  return new Color(0.3 + r * 0.7, 0.3 + g * 0.7, 0.3 + b * 0.7, 1);
}

function neutralizeModelLighting(viewer, entity) {
  const scene = viewer.scene;
  scene.highDynamicRange = false;

  if (entity.model?.imageBasedLightingFactor) {
    entity.model.imageBasedLightingFactor = new Cartesian2(0.0, 0.0);
  }

  if (DirectionalLight) {
    scene.light = new DirectionalLight({
      direction: Cartesian3.normalize(new Cartesian3(0.5, 0.5, -1.0), new Cartesian3()),
      intensity: 1.0,
    });
  }

  if (entity.model?.color) entity.model.color = Color.WHITE;
  if (entity.model?.colorBlendMode) entity.model.colorBlendMode = ColorBlendMode.REPLACE;
  if (entity.model?.shadows !== undefined) entity.model.shadows = ShadowMode.DISABLED;

  if (CustomShader && LightingModel) {
    entity.model.customShader = new CustomShader({
      lightingModel: LightingModel.UNLIT,
    });
  }
}

function normalizeTraceItem(item, truckIdFallback) {
  const coordinates = item?.geometry?.coordinates || item?.coordinates || item?.coord || (Array.isArray(item) ? item : null);
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  return {
    truckId: String(item?.truck_id || item?.truckId || item?.properties?.truck_id || truckIdFallback || "unknown"),
    lon: Number(coordinates[0]),
    lat: Number(coordinates[1]),
    height: coordinates[2] != null ? Number(coordinates[2]) : 0,
    timestamp: parseTimestamp(item?.timestamp || item?.time || item?.ts || item?.properties?.timestamp || item?.properties?.ts),
    heading: item?.properties?.heading != null ? Number(item.properties.heading) : item?.heading != null ? Number(item.heading) : undefined,
    speed: item?.properties?.speed != null ? Number(item.properties.speed) : item?.speed != null ? Number(item.speed) : undefined,
  };
}

function normalizeEventItem(item, truckIdFallback) {
  const coordinates = item?.geometry?.coordinates || item?.coordinates || item?.coord;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const properties = item?.properties || {};
  const eventType = item?.event_type || item?.eventType || properties?.event_type || properties?.eventType;

  return {
    id: String(item?.id || properties?.id || `${truckIdFallback || "unknown"}-${eventType || "event"}-${parseTimestamp(item?.timestamp || item?.time || item?.ts)}`),
    truckId: String(item?.truck_id || item?.truckId || properties?.truck_id || truckIdFallback || "unknown"),
    lon: Number(coordinates[0]),
    lat: Number(coordinates[1]),
    height: coordinates[2] != null ? Number(coordinates[2]) : 0,
    timestamp: parseTimestamp(item?.timestamp || item?.time || item?.ts || properties?.timestamp || properties?.ts),
    eventType: eventType ? String(eventType) : undefined,
    iconUrl: item?.icon_url || item?.iconUrl || properties?.icon_url || properties?.iconUrl,
    properties,
  };
}

function pushTraceCollection(target, input, truckIdFallback) {
  if (!input) return;

  const append = (point) => {
    if (!point) return;
    const key = point.truckId;
    const list = target.get(key) || [];
    list.push(point);
    target.set(key, list);
  };

  if (Array.isArray(input)) {
    input.forEach((item) => append(normalizeTraceItem(item, truckIdFallback)));
    return;
  }

  if (input?.type === "FeatureCollection" && Array.isArray(input.features)) {
    input.features.forEach((feature) => append(normalizeTraceItem(feature, truckIdFallback)));
    return;
  }

  if (input?.type === "Feature") {
    append(normalizeTraceItem(input, truckIdFallback));
    return;
  }

  if (typeof input === "object") {
    Object.entries(input).forEach(([truckId, value]) => {
      pushTraceCollection(target, value, truckId);
    });
  }
}

function pushEventCollection(target, input, truckIdFallback) {
  if (!input) return;

  const append = (event) => {
    if (!event) return;
    const key = event.truckId;
    const list = target.get(key) || [];
    list.push(event);
    target.set(key, list);
  };

  if (Array.isArray(input)) {
    input.forEach((item) => append(normalizeEventItem(item, truckIdFallback)));
    return;
  }

  if (input?.type === "FeatureCollection" && Array.isArray(input.features)) {
    input.features.forEach((feature) => append(normalizeEventItem(feature, truckIdFallback)));
    return;
  }

  if (input?.type === "Feature") {
    append(normalizeEventItem(input, truckIdFallback));
    return;
  }

  if (typeof input === "object") {
    Object.entries(input).forEach(([truckId, value]) => {
      pushEventCollection(target, value, truckId);
    });
  }
}

function normalizeResponse(raw) {
  const tracesByTruck = new Map();
  const eventsByTruck = new Map();

  if (!raw) return { tracesByTruck, eventsByTruck };

  if (Array.isArray(raw?.trucks)) {
    raw.trucks.forEach((truck) => {
      const truckId = String(truck?.truck_id || truck?.truckId || "unknown");
      pushTraceCollection(tracesByTruck, truck?.trace || truck?.traces || truck?.traceGeoJson, truckId);
      pushEventCollection(eventsByTruck, truck?.events || truck?.eventGeoJson, truckId);
    });
    return { tracesByTruck, eventsByTruck };
  }

  pushTraceCollection(tracesByTruck, raw?.trace || raw?.traces || raw?.traceGeoJson);
  pushEventCollection(eventsByTruck, raw?.events || raw?.eventGeoJson);

  return { tracesByTruck, eventsByTruck };
}

function buildMockResponse(truckIdFilter, cycle) {
  const now = Date.now();
  const useSingle = truckIdFilter && truckIdFilter.trim() !== "";
  const ids = useSingle ? [truckIdFilter.trim()] : MOCK_TRUCK_IDS;

  const base = {
    TRUCK_101: { lon: -122.4194, lat: 37.7749, heading: 90 },
    TRUCK_202: { lon: -122.4094, lat: 37.7799, heading: 130 },
    TRUCK_303: { lon: -122.4294, lat: 37.7699, heading: 45 },
  };

  const trucks = ids.flatMap((id, truckIndex) => {
    const normalizedIndex = MOCK_TRUCK_IDS.includes(id) ? MOCK_TRUCK_IDS.indexOf(id) : truckIndex;
    const seed = base[id] || {
      lon: -122.4194 + truckIndex * 0.01,
      lat: 37.7749 + truckIndex * 0.005,
      heading: 90 + truckIndex * 25,
    };

    const shouldSkipTrace = !useSingle && ((normalizedIndex === 1 && cycle % 5 === 3) || (normalizedIndex === 2 && cycle % 7 >= 5));
    if (shouldSkipTrace) return [];

    const progressBase = cycle * 10;
    const traceFeatures = [];
    const pointCount = 10;

    for (let i = 0; i < pointCount; i += 1) {
      const sample = progressBase + i;
      const lon = seed.lon + sample * 0.00018 + Math.sin((sample + normalizedIndex) * 0.25) * 0.00006;
      const lat = seed.lat + sample * 0.0001 + Math.cos((sample + normalizedIndex) * 0.2) * 0.00005;
      const heading = (seed.heading + sample * 2.8) % 360;

      traceFeatures.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lon, lat, 0],
        },
        properties: {
          truck_id: id,
          ts: now - (pointCount - 1 - i) * 1000,
          heading,
          speed: Number((8 + normalizedIndex * 2 + i * 0.25).toFixed(2)),
        },
      });
    }

    const lastTrace = traceFeatures[traceFeatures.length - 1];
    const lastCoord = lastTrace.geometry.coordinates;
    const eventFeatures = [];

    if (cycle % 2 === normalizedIndex % 2) {
      eventFeatures.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lastCoord[0] - 0.00045, lastCoord[1] + 0.00028, 0],
        },
        properties: {
          id: `${id}-hard-brake-${cycle}`,
          truck_id: id,
          ts: now - 3500,
          event_type: "hard_brake",
          severity: "medium",
          note: `Mock hard brake ${cycle}`,
        },
      });
    }

    if (cycle % 3 === (normalizedIndex + 1) % 3) {
      eventFeatures.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lastCoord[0] + 0.0003, lastCoord[1] - 0.00025, 0],
        },
        properties: {
          id: `${id}-manual-mark-${cycle}`,
          truck_id: id,
          ts: now - 1200,
          event_type: "manual_mark",
          severity: "low",
          note: `Mock manual mark ${cycle}`,
        },
      });
    }

    return [{
      truck_id: id,
      traceGeoJson: {
        type: "FeatureCollection",
        features: traceFeatures,
      },
      eventGeoJson: {
        type: "FeatureCollection",
        features: eventFeatures,
      },
    }];
  });

  return { trucks };
}

function clonePoint(point) {
  return {
    truckId: point.truckId,
    lon: point.lon,
    lat: point.lat,
    height: point.height || 0,
    timestamp: point.timestamp,
    heading: point.heading,
    speed: point.speed,
  };
}

function getBridgeReason(gapMs, wasPaused) {
  if (wasPaused) return "paused";
  if (gapMs > POLL_MS * 1.05) return "outage";
  return null;
}

function getEventVisual(eventType) {
  return EVENT_TYPE_STYLES[eventType] || EVENT_TYPE_STYLES.default;
}

function LiveTrace() {
  const viewerRef = useAppStore.getState().viewerRef;

  const liveTraceEndpoint = useMemo(() => {
    const store = useAppStore.getState();
    return store?.liveTraceEndpoint || store?.config?.liveTraceEndpoint || "/api/live-trace";
  }, []);

  const eventIconMap = useMemo(() => {
    const store = useAppStore.getState();
    return store?.liveTraceEventIcons || store?.config?.liveTraceEventIcons || {};
  }, []);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [truckIdFilter, setTruckIdFilter] = useState("");
  const [statusText, setStatusText] = useState("Idle");
  const [isPolling, setIsPolling] = useState(false);
  const [truckCount, setTruckCount] = useState(0);
  const [useMockData, setUseMockData] = useState(true);

  const mountedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const renderTimerRef = useRef(null);
  const activeRequestRef = useRef(null);
  const memoryRef = useRef(new Map());
  const entityIdsRef = useRef(new Set());
  const didAutoZoomRef = useRef(false);
  const mockCycleRef = useRef(0);
  const enabledRef = useRef(false);
  const previousEnabledRef = useRef(false);
  const pausedAtRef = useRef(null);

  const getViewer = useCallback(() => {
    const viewer = viewerRef?.current || null;
    if (!viewer) return null;
    if (typeof viewer.isDestroyed === "function" && viewer.isDestroyed()) return null;
    return viewer;
  }, [viewerRef]);

  const requestRenderSafe = useCallback(() => {
    const viewer = getViewer();
    if (!viewer) return;
    if (viewer.scene && typeof viewer.scene.requestRender === "function") {
      viewer.scene.requestRender();
    }
  }, [getViewer]);

  const getTruckMemory = useCallback((truckId) => memoryRef.current.get(truckId) || null, []);

  const shouldShowTruck = useCallback(
    (truckId) => {
      if (!enabledRef.current) return false;
      const filter = truckIdFilter.trim();
      if (!filter) return true;
      return truckId === filter;
    },
    [truckIdFilter],
  );

  const interpolatePointInSegment = useCallback((segment, wallNow) => {
    if (!segment?.points?.length) return null;
    if (segment.points.length === 1) return segment.points[0];

    const playbackStartWall = segment.playbackStartedAt ?? wallNow;
    const playbackDuration = Math.max(1, segment.playbackDurationMs ?? POLL_MS);
    const sourceStartTs = segment.sourceStartTs ?? segment.points[0].timestamp;
    const sourceEndTs = segment.sourceEndTs ?? segment.points[segment.points.length - 1].timestamp;
    const clampedProgress = Math.min(1, Math.max(0, (wallNow - playbackStartWall) / playbackDuration));
    const targetTs = sourceStartTs + (sourceEndTs - sourceStartTs) * clampedProgress;

    for (let i = 1; i < segment.points.length; i += 1) {
      const p1 = segment.points[i - 1];
      const p2 = segment.points[i];
      if (targetTs <= p2.timestamp || i === segment.points.length - 1) {
        const interval = Math.max(1, p2.timestamp - p1.timestamp);
        const t = Math.min(1, Math.max(0, (targetTs - p1.timestamp) / interval));
        const heading1 = Number.isFinite(p1.heading) ? p1.heading : p2.heading ?? 0;
        const heading2 = Number.isFinite(p2.heading) ? p2.heading : heading1;
        return {
          truckId: p2.truckId,
          lon: p1.lon + (p2.lon - p1.lon) * t,
          lat: p1.lat + (p2.lat - p1.lat) * t,
          height: (p1.height || 0) + ((p2.height || 0) - (p1.height || 0)) * t,
          heading: heading1 + (heading2 - heading1) * t,
          speed: p2.speed ?? p1.speed,
          timestamp: targetTs,
        };
      }
    }

    return segment.points[segment.points.length - 1];
  }, []);

  const interpolateTruckPoint = useCallback(
    (truckId) => {
      const truck = memoryRef.current.get(truckId);
      if (!truck || !truck.segments.length) return null;
      const wallNow = Date.now();
      const currentSegment = truck.segments[truck.segments.length - 1];

      if (truck.outageStartedAt != null) {
        return truck.lastPoint || currentSegment.points[currentSegment.points.length - 1] || null;
      }

      const currentPoint = interpolatePointInSegment(currentSegment, wallNow);
      return currentPoint || truck.lastPoint || null;
    },
    [interpolatePointInSegment],
  );

  const ensureTruckMemory = useCallback((truckId) => {
    const existing = memoryRef.current.get(truckId);
    if (existing) return existing;

    const created = {
      truckId,
      color: getTruckColor(truckId),
      segments: [],
      events: new Map(),
      eventOrder: [],
      outageStartedAt: null,
      lastDataAt: null,
      lastPoint: null,
      expectedNextAt: null,
      missingSince: null,
      lastSeenPollAt: null,
      lastBridgeReason: null,
    };

    memoryRef.current.set(truckId, created);
    if (mountedRef.current) setTruckCount(memoryRef.current.size);
    return created;
  }, []);

  const setEntityVisible = useCallback(
    (entityId, visible) => {
      const viewer = getViewer();
      if (!viewer) return;
      const entity = viewer.entities.getById(entityId);
      if (entity) entity.show = visible;
    },
    [getViewer],
  );

  const setAllEntitiesVisible = useCallback(
    (visible) => {
      entityIdsRef.current.forEach((id) => setEntityVisible(id, visible));
      requestRenderSafe();
    },
    [requestRenderSafe, setEntityVisible],
  );

  const removeAllLiveTraceEntities = useCallback(() => {
    const viewer = getViewer();
    if (!viewer) return;

    entityIdsRef.current.forEach((id) => {
      const entity = viewer.entities.getById(id);
      if (entity) viewer.entities.remove(entity);
    });

    entityIdsRef.current.clear();
    requestRenderSafe();
  }, [getViewer, requestRenderSafe]);

  const appendTracePoints = useCallback(
    (truckId, points) => {
      if (!points?.length) return false;

      const truck = ensureTruckMemory(truckId);
      const now = Date.now();
      const sorted = [...points]
        .map(clonePoint)
        .sort((a, b) => a.timestamp - b.timestamp)
        .filter((point) => Number.isFinite(point.lon) && Number.isFinite(point.lat));
      if (!sorted.length) return false;

      const uniquePoints = [];
      const seenBatch = new Set();
      sorted.forEach((point) => {
        const key = `${point.timestamp}-${point.lon}-${point.lat}`;
        if (!seenBatch.has(key)) {
          seenBatch.add(key);
          uniquePoints.push(point);
        }
      });
      if (!uniquePoints.length) return false;

      const previousLastPoint = truck.lastPoint;
      const previousSegment = truck.segments[truck.segments.length - 1] || null;
      const firstNewTs = uniquePoints[0].timestamp;
      const previousLastTs = previousLastPoint?.timestamp ?? null;
      const gapMs = previousLastTs != null ? Math.max(0, firstNewTs - previousLastTs) : 0;
      const wasPaused = previousEnabledRef.current === false && pausedAtRef.current != null;
      const bridgeReason = previousLastTs != null ? getBridgeReason(gapMs, wasPaused || truck.lastBridgeReason === "paused") : null;
      const needNewSegment = !previousSegment || bridgeReason != null;

      let targetSegment = previousSegment;
      if (needNewSegment) {
        targetSegment = {
          id: `${truckId}-${now}`,
          points: [],
          sourceStartTs: uniquePoints[0].timestamp,
          sourceEndTs: uniquePoints[uniquePoints.length - 1].timestamp,
          playbackStartedAt: now,
          playbackDurationMs: Math.max(1, uniquePoints[uniquePoints.length - 1].timestamp - uniquePoints[0].timestamp, POLL_MS),
          bridgeFromPrevious: previousLastPoint ? {
            from: clonePoint(previousLastPoint),
            to: clonePoint(uniquePoints[0]),
            reason: bridgeReason,
          } : null,
        };
        truck.segments.push(targetSegment);
      }

      const known = new Set(targetSegment.points.map((p) => `${p.timestamp}-${p.lon}-${p.lat}`));
      let didAppend = false;
      uniquePoints.forEach((point) => {
        const key = `${point.timestamp}-${point.lon}-${point.lat}`;
        if (!known.has(key)) {
          targetSegment.points = [...targetSegment.points, clonePoint(point)];
          known.add(key);
          didAppend = true;
        }
      });
      if (!didAppend) return false;

      targetSegment.points.sort((a, b) => a.timestamp - b.timestamp);
      targetSegment.sourceStartTs = targetSegment.points[0].timestamp;
      targetSegment.sourceEndTs = targetSegment.points[targetSegment.points.length - 1].timestamp;
      targetSegment.playbackStartedAt = now;
      targetSegment.playbackDurationMs = Math.max(1, targetSegment.sourceEndTs - targetSegment.sourceStartTs, POLL_MS);

      truck.lastDataAt = now;
      truck.lastSeenPollAt = now;
      truck.expectedNextAt = now + POLL_MS;
      truck.outageStartedAt = null;
      truck.missingSince = null;
      truck.lastPoint = clonePoint(targetSegment.points[targetSegment.points.length - 1]);
      truck.lastBridgeReason = null;
      return true;
    },
    [ensureTruckMemory],
  );

  const appendEvents = useCallback(
    (truckId, events) => {
      if (!events?.length) return;
      const truck = ensureTruckMemory(truckId);

      events
        .map((event) => ({ ...event, iconUrl: event.iconUrl || eventIconMap[event.eventType] || null }))
        .forEach((event) => {
          if (!truck.events.has(event.id)) {
            truck.events.set(event.id, event);
            truck.eventOrder.push(event.id);
          }
        });
    },
    [ensureTruckMemory, eventIconMap],
  );

  const updateOutageStates = useCallback((trucksWithFreshData) => {
    const now = Date.now();
    const activeFilter = truckIdFilter.trim();

    memoryRef.current.forEach((truck, truckId) => {
      const inScope = !activeFilter || activeFilter === truckId;
      if (!inScope) return;

      if (trucksWithFreshData.has(truckId)) {
        truck.outageStartedAt = null;
        truck.missingSince = null;
        truck.expectedNextAt = now + POLL_MS;
        return;
      }

      if (truck.lastDataAt == null) {
        if (activeFilter && activeFilter === truckId && truck.missingSince == null) {
          truck.missingSince = now;
          truck.outageStartedAt = now;
          truck.lastBridgeReason = "outage";
        }
        return;
      }

      if (truck.missingSince == null && truck.expectedNextAt != null && now >= truck.expectedNextAt) {
        truck.missingSince = truck.expectedNextAt;
      }

      if (truck.missingSince != null && truck.outageStartedAt == null) {
        truck.outageStartedAt = truck.missingSince;
        truck.lastBridgeReason = "outage";
      }
    });
  }, [truckIdFilter]);

  const ensureTruckEntity = useCallback(
    (truck) => {
      const viewer = getViewer();
      if (!viewer) return;

      const id = `live-trace-truck-${truck.truckId}`;
      let entity = viewer.entities.getById(id);

      if (!entity) {
        entity = viewer.entities.add({
          id,
          position: new CallbackProperty(() => {
            const point = interpolateTruckPoint(truck.truckId);
            if (!point) return undefined;
            return Cartesian3.fromDegrees(point.lon, point.lat, point.height || 0);
          }, false),
          orientation: new CallbackProperty(() => {
            const point = interpolateTruckPoint(truck.truckId);
            if (!point) return undefined;
            const headingRad = CesiumMath.toRadians(Number.isFinite(point.heading) ? point.heading : 0);
            return Transforms.headingPitchRollQuaternion(Cartesian3.fromDegrees(point.lon, point.lat, point.height || 0), new HeadingPitchRoll(headingRad, 0, 0));
          }, false),
          model: {
            uri: TRUCK_MODEL_URL,
            scale: TRUCK_MODEL_SCALE,
            minimumPixelSize: 64,
            maximumScale: 200,
            color: Color.WHITE,
            colorBlendMode: ColorBlendMode.REPLACE,
            shadows: ShadowMode.DISABLED,
          },
          label: {
            text: new CallbackProperty(() => {
              const currentTruck = getTruckMemory(truck.truckId);
              if (!currentTruck?.outageStartedAt) return "";
              return formatOutage(Math.max(0, Math.floor((Date.now() - currentTruck.outageStartedAt) / 1000)));
            }, false),
            show: new CallbackProperty(() => {
              const currentTruck = getTruckMemory(truck.truckId);
              return currentTruck?.outageStartedAt != null && shouldShowTruck(truck.truckId);
            }, false),
            font: "bold 13px sans-serif",
            fillColor: Color.RED,
            outlineColor: Color.WHITE,
            outlineWidth: 2,
            style: LabelStyle.FILL_AND_OUTLINE,
            pixelOffset: OUTAGE_LABEL_OFFSET,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            horizontalOrigin: HorizontalOrigin.CENTER,
            verticalOrigin: VerticalOrigin.BOTTOM,
          },
        });

        neutralizeModelLighting(viewer, entity);
      }

      const point = interpolateTruckPoint(truck.truckId);
      entity.description = buildTruckDescription(truck.truckId, point || truck.lastPoint, truck.outageStartedAt);
      entity.show = shouldShowTruck(truck.truckId) && !!truck.lastPoint;

      if (entity.model) {
        entity.model.color = truck.outageStartedAt == null ? Color.LIME : Color.RED;
      }

      entityIdsRef.current.add(id);
    },
    [getTruckMemory, getViewer, interpolateTruckPoint, shouldShowTruck],
  );

  const ensureTraceSegmentEntities = useCallback(
    (truck) => {
      const viewer = getViewer();
      if (!viewer) return;
      const visible = shouldShowTruck(truck.truckId);

      truck.segments.forEach((segment) => {
        const lineId = `live-trace-line-${truck.truckId}-${segment.id}`;
        let lineEntity = viewer.entities.getById(lineId);
        const positionsSnapshot = segment.points.map((p) => Cartesian3.fromDegrees(p.lon, p.lat, p.height || 0));

        if (!lineEntity && positionsSnapshot.length) {
          lineEntity = viewer.entities.add({
            id: lineId,
            polyline: {
              positions: positionsSnapshot,
              width: 2,
              material: truck.color.withAlpha(0.9),
              clampToGround: false,
            },
            show: visible,
          });
        } else if (lineEntity) {
          lineEntity.polyline.positions = positionsSnapshot;
          lineEntity.show = visible;
        }
        if (lineEntity) entityIdsRef.current.add(lineId);

        segment.points.forEach((point, pointIndex) => {
          const pointId = `live-trace-point-${truck.truckId}-${segment.id}-${pointIndex}-${point.timestamp}`;
          let pointEntity = viewer.entities.getById(pointId);

          if (!pointEntity) {
            pointEntity = viewer.entities.add({
              id: pointId,
              position: Cartesian3.fromDegrees(point.lon, point.lat, point.height || 0),
              point: {
                pixelSize: 3,
                color: truck.color.withAlpha(0.95),
                outlineColor: Color.WHITE,
                outlineWidth: 1,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
              },
              show: visible,
            });
          } else {
            pointEntity.show = visible;
          }

          entityIdsRef.current.add(pointId);
        });

        if (segment.bridgeFromPrevious?.from && segment.bridgeFromPrevious?.to) {
          const jumpId = `live-trace-jump-${truck.truckId}-${segment.id}`;
          let jumpEntity = viewer.entities.getById(jumpId);
          const jumpPositions = [
            Cartesian3.fromDegrees(segment.bridgeFromPrevious.from.lon, segment.bridgeFromPrevious.from.lat, segment.bridgeFromPrevious.from.height || 0),
            Cartesian3.fromDegrees(segment.bridgeFromPrevious.to.lon, segment.bridgeFromPrevious.to.lat, segment.bridgeFromPrevious.to.height || 0),
          ];

          if (!jumpEntity) {
            jumpEntity = viewer.entities.add({
              id: jumpId,
              polyline: {
                positions: jumpPositions,
                width: 2,
                material: new PolylineDashMaterialProperty({
                  color: truck.color.withAlpha(0.85),
                  dashLength: 12,
                }),
                clampToGround: false,
              },
              show: visible,
            });
          } else {
            jumpEntity.polyline.positions = jumpPositions;
            jumpEntity.show = visible;
          }

          entityIdsRef.current.add(jumpId);
        }
      });
    },
    [getViewer, shouldShowTruck],
  );

  const ensureEventEntities = useCallback(
    (truck) => {
      const viewer = getViewer();
      if (!viewer) return;
      const visible = shouldShowTruck(truck.truckId);

      truck.eventOrder.forEach((eventId) => {
        const event = truck.events.get(eventId);
        if (!event) return;

        const id = `live-trace-event-${truck.truckId}-${event.id}`;
        let entity = viewer.entities.getById(id);
        const visual = getEventVisual(event.eventType);

        if (!entity) {
          entity = viewer.entities.add({
            id,
            position: Cartesian3.fromDegrees(event.lon, event.lat, event.height || 0),
            billboard: event.iconUrl
              ? {
                  image: event.iconUrl,
                  scale: 0.7,
                  verticalOrigin: VerticalOrigin.BOTTOM,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                }
              : undefined,
            point: !event.iconUrl
              ? {
                  pixelSize: 14,
                  color: visual.color.withAlpha(0.95),
                  outlineColor: Color.BLACK,
                  outlineWidth: 2,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                }
              : undefined,
            label: !event.iconUrl
              ? {
                  text: visual.short,
                  font: "bold 10px sans-serif",
                  fillColor: Color.BLACK,
                  outlineColor: Color.WHITE,
                  outlineWidth: 2,
                  style: LabelStyle.FILL_AND_OUTLINE,
                  pixelOffset: new Cartesian2(0, -1),
                  horizontalOrigin: HorizontalOrigin.CENTER,
                  verticalOrigin: VerticalOrigin.CENTER,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                }
              : undefined,
            description: buildEventDescription(event),
            properties: event.properties,
            show: visible,
          });
        } else {
          entity.show = visible;
        }

        entityIdsRef.current.add(id);
      });
    },
    [getViewer, shouldShowTruck],
  );

  const syncEntitiesToViewer = useCallback(() => {
    const viewer = getViewer();
    if (!viewer) return;

    memoryRef.current.forEach((truck) => {
      ensureTruckEntity(truck);
      ensureTraceSegmentEntities(truck);
      ensureEventEntities(truck);
    });

    requestRenderSafe();
  }, [ensureEventEntities, ensureTraceSegmentEntities, ensureTruckEntity, getViewer, requestRenderSafe]);

  const getAllPositionsForZoom = useCallback(() => {
    const positions = [];
    memoryRef.current.forEach((truck) => {
      if (!shouldShowTruck(truck.truckId)) return;
      truck.segments.forEach((segment) => {
        segment.points.forEach((point) => {
          positions.push(Cartesian3.fromDegrees(point.lon, point.lat, point.height || 0));
        });
      });
      truck.eventOrder.forEach((eventId) => {
        const event = truck.events.get(eventId);
        if (event) positions.push(Cartesian3.fromDegrees(event.lon, event.lat, event.height || 0));
      });
    });
    return positions;
  }, [shouldShowTruck]);

  const zoomToLiveTrace = useCallback(() => {
    const viewer = getViewer();
    if (!viewer) return;

    const positions = getAllPositionsForZoom();
    if (!positions.length) return;

    const sphere = BoundingSphere.fromPoints(positions);
    if (!defined(sphere) || !Number.isFinite(sphere.radius)) return;

    viewer.camera.flyToBoundingSphere(sphere, {
      duration: 1.2,
      offset: new HeadingPitchRange(viewer.camera.heading, CesiumMath.toRadians(-45), Math.max(sphere.radius * 2.2, 250)),
    });

    requestRenderSafe();
  }, [getAllPositionsForZoom, getViewer, requestRenderSafe]);

  const fetchLiveTrace = useCallback(async () => {
    if (!mountedRef.current || !enabledRef.current) return;

    setIsPolling(true);
    setStatusText(useMockData ? "Polling mock data..." : "Polling API...");

    try {
      let raw;
      if (useMockData) {
        mockCycleRef.current += 1;
        raw = buildMockResponse(truckIdFilter, mockCycleRef.current);
      } else {
        if (activeRequestRef.current) activeRequestRef.current.abort();
        activeRequestRef.current = new AbortController();

        const params = new URLSearchParams();
        if (truckIdFilter.trim()) params.set("truck_id", truckIdFilter.trim());

        const url = `${liveTraceEndpoint}${params.toString() ? `?${params.toString()}` : ""}`;
        const response = await fetch(url, {
          method: "GET",
          signal: activeRequestRef.current.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        raw = await response.json();
      }

      const normalized = normalizeResponse(raw);
      const trucksWithFreshData = new Set();

      normalized.tracesByTruck.forEach((points, truckId) => {
        if (appendTracePoints(truckId, points)) {
          trucksWithFreshData.add(truckId);
        }
      });

      normalized.eventsByTruck.forEach((events, truckId) => {
        if (events.length) appendEvents(truckId, events);
      });

      if (truckIdFilter.trim()) {
        ensureTruckMemory(truckIdFilter.trim());
      }

      updateOutageStates(trucksWithFreshData);
      syncEntitiesToViewer();

      if (!didAutoZoomRef.current && getAllPositionsForZoom().length > 0) {
        didAutoZoomRef.current = true;
        window.setTimeout(() => {
          zoomToLiveTrace();
        }, 50);
      }

      if (mountedRef.current) {
        setTruckCount(memoryRef.current.size);
        const scopedCount = Array.from(memoryRef.current.keys()).filter((truckId) => shouldShowTruck(truckId)).length;
        setStatusText(`${useMockData ? "Mock" : "Live"} • ${scopedCount} visible truck(s) • ${memoryRef.current.size} in memory`);
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Live Trace error:", err);
        if (mountedRef.current) {
          setStatusText(err?.message || "Polling error");
          showToast(err?.message || "Live Trace error", Type.NOTIFICATION.ERROR);
        }
      }
    } finally {
      if (mountedRef.current) setIsPolling(false);
    }
  }, [appendEvents, appendTracePoints, ensureTruckMemory, getAllPositionsForZoom, liveTraceEndpoint, shouldShowTruck, syncEntitiesToViewer, truckIdFilter, updateOutageStates, useMockData, zoomToLiveTrace]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current != null) return;
    fetchLiveTrace();
    pollTimerRef.current = window.setInterval(() => {
      fetchLiveTrace();
    }, POLL_MS);
  }, [fetchLiveTrace]);

  const stopRenderTimer = useCallback(() => {
    if (renderTimerRef.current != null) {
      window.clearInterval(renderTimerRef.current);
      renderTimerRef.current = null;
    }
  }, []);

  const startRenderTimer = useCallback(() => {
    if (renderTimerRef.current != null) return;
    renderTimerRef.current = window.setInterval(() => {
      if (!mountedRef.current || !enabledRef.current) return;

      memoryRef.current.forEach((truck) => {
        const viewer = getViewer();
        if (!viewer) return;
        const entity = viewer.entities.getById(`live-trace-truck-${truck.truckId}`);
        if (!entity) return;

        const point = interpolateTruckPoint(truck.truckId);
        entity.description = buildTruckDescription(truck.truckId, point || truck.lastPoint, truck.outageStartedAt);
        entity.show = shouldShowTruck(truck.truckId) && !!truck.lastPoint;

        if (entity.model) {
          entity.model.color = truck.outageStartedAt == null ? Color.LIME : Color.RED;
        }
      });

      requestRenderSafe();
    }, REQUEST_RENDER_MS);
  }, [getViewer, interpolateTruckPoint, requestRenderSafe, shouldShowTruck]);

  const clearMemory = useCallback(() => {
    memoryRef.current.clear();
    didAutoZoomRef.current = false;
    mockCycleRef.current = 0;

    if (mountedRef.current) {
      setTruckCount(0);
      setStatusText("Memory cleared");
    }

    removeAllLiveTraceEntities();
  }, [removeAllLiveTraceEntities]);

  const handleToggle = useCallback(() => {
    const viewer = getViewer();
    if (!viewer) {
      showToast("Cesium viewer not found.", Type.NOTIFICATION.ERROR);
      return;
    }
    setEnabled((prev) => !prev);
  }, [getViewer]);

  const handleZoomNow = useCallback(() => {
    zoomToLiveTrace();
  }, [zoomToLiveTrace]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    setAllEntitiesVisible(false);

    return () => {
      mountedRef.current = false;
      stopPolling();
      stopRenderTimer();
      removeAllLiveTraceEntities();
    };
  }, [removeAllLiveTraceEntities, setAllEntitiesVisible, stopPolling, stopRenderTimer]);

  useEffect(() => {
    if (!mountedRef.current) return;

    if (enabled) {
      if (!previousEnabledRef.current) {
        memoryRef.current.forEach((truck) => {
          truck.lastBridgeReason = pausedAtRef.current != null ? "paused" : null;
        });
      }
      setAllEntitiesVisible(true);
      syncEntitiesToViewer();
      startPolling();
      startRenderTimer();
      setStatusText((prev) => (prev === "Stopped" ? "Starting..." : prev));
      pausedAtRef.current = null;
    } else {
      pausedAtRef.current = Date.now();
      stopPolling();
      stopRenderTimer();
      setAllEntitiesVisible(false);
      setStatusText("Stopped");
      requestRenderSafe();
    }

    previousEnabledRef.current = enabled;
  }, [enabled, requestRenderSafe, setAllEntitiesVisible, startPolling, startRenderTimer, stopPolling, stopRenderTimer, syncEntitiesToViewer]);

  useEffect(() => {
    if (!mountedRef.current) return;
    if (!enabled) return;
    syncEntitiesToViewer();
    didAutoZoomRef.current = false;
  }, [enabled, syncEntitiesToViewer, truckIdFilter]);

  return (
    <Popover.Root positioning={{ placement: "bottom-end" }} size="lg" open={popoverOpen} onOpenChange={(e) => setPopoverOpen(e.open)}>
      <Popover.Trigger asChild>
        <IconButton userSelect="none" rounded="full" boxShadow="lg" bg="Background">
          <Tooltip content="Live Trace">
            <Box color={enabled ? "green.500" : "red.500"}>
              <LuCircleDot />
            </Box>
          </Tooltip>
        </IconButton>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content width="460px">
            <Popover.Arrow />
            <Popover.Body p={3}>
              <VStack align="stretch" gap={3}>
                <Text fontSize="md" fontWeight="semibold">
                  Live Trace
                </Text>

                <Separator />

                <VStack align="stretch" gap={2}>
                  <Text fontSize="sm">Truck ID</Text>
                  <Input value={truckIdFilter} onChange={(e) => setTruckIdFilter(e.target.value)} placeholder="Leave empty for all trucks" size="sm" />
                </VStack>

                <HStack justify="space-between">
                  <Button size="sm" colorPalette={enabled ? "red" : "green"} onClick={handleToggle}>
                    {enabled ? <LuPause /> : <LuPlay />}
                    {enabled ? "Stop" : "Start"}
                  </Button>

                  <Button size="sm" variant="outline" onClick={fetchLiveTrace} disabled={!enabled || isPolling}>
                    <LuRefreshCw />
                    Refresh
                  </Button>

                  <Button size="sm" variant="outline" onClick={handleZoomNow} disabled={truckCount === 0}>
                    <LuLocateFixed />
                    Zoom
                  </Button>

                  <Button size="sm" variant="outline" colorPalette="red" onClick={clearMemory}>
                    <LuTrash2 />
                    Clear
                  </Button>
                </HStack>

                <Separator />

                <HStack justify="space-between">
                  <Text fontSize="sm" color="fg.muted">
                    Data source
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      setUseMockData((prev) => !prev);
                      didAutoZoomRef.current = false;
                    }}
                  >
                    {useMockData ? "Mock GeoJSON" : "Web Service"}
                  </Button>
                </HStack>

                <VStack align="stretch" gap={1}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">
                      Polling
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      Every {POLL_MS / 1000}s
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">
                      Trucks in memory
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {truckCount}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">
                      Status
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" maxW="260px" truncate>
                      {statusText}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">
                      Model
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" maxW="220px" truncate>
                      gatiktruck.glb
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">
                      API
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" maxW="220px" truncate>
                      {useMockData ? "Using built-in mock GeoJSON" : liveTraceEndpoint}
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default LiveTrace;
